import { useState, useEffect, useCallback } from 'react';
import { getSurvey, getTestSurvey, saveAnswer, updateAnswer } from '../api';

const messageFromSaveError = (error) => {
  const generic = 'Something went wrong saving your answer. Please try again.';
  if (error?.response?.status !== 400) return generic;

  const data = error.response.data;
  if (typeof data === 'string') return data;

  const first = data && typeof data === 'object' ? Object.values(data)[0] : null;
  if (Array.isArray(first)) return String(first[0]);
  return first ? String(first) : generic;
};

export const useSurvey = (inviteID, inTestMode = false) => {
  const [status, setStatus] = useState('loading');
  const [saveError, setSaveError] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);

  const currentQuestion = status !== 'completed' && survey ? survey.questions[questionIndex] : null;
  const hasPrev = status !== 'completed' && questionIndex > 0;
  const hasNext = status !== 'completed' && survey && questionIndex < survey.questions.length - 1;
  const isAnswered = currentQuestion && !!currentQuestion.answer && !!currentQuestion.answer.answer;

  useEffect(() => {
    if (!inviteID) return;

    const surveyPromise = inTestMode 
      ? getTestSurvey(inviteID)
      : getSurvey(inviteID);

    surveyPromise
      .then(({ data: survey }) => {
        const firstUnansweredIndex = survey.questions.findIndex((q) => !q.answer);
        if (firstUnansweredIndex === -1) {
          setSurvey(survey);
          setStatus('completed');
        } else {
          setQuestionIndex(firstUnansweredIndex);
          setSurvey(survey);
          setStatus('loaded');
        }
      })
      .catch(() => setStatus('not-found'));
  }, [inviteID, inTestMode]);

  const updateLocalAnswer = useCallback((answer) => {
    setSaveError(null);
    setSurvey(prev => {
      const updated = { ...prev };
      const questions = [...prev.questions];
      const question = { ...questions[questionIndex] };

      question.answer = question.answer
        ? { ...question.answer, answer }
        : { answer };

      questions[questionIndex] = question;
      updated.questions = questions;
      return updated;
    });
  }, [questionIndex]);

  const goBack = useCallback(() => {
    if (hasPrev) {
      setQuestionIndex(prev => prev - 1);
    }
  }, [hasPrev]);

  const goForward = useCallback(async () => {
    if (!currentQuestion) return;

    // Allow comment questions to proceed without answer
    if (currentQuestion.q_type !== 'comment' && !isAnswered) {
      return;
    }

    setSaveError(null);

    try {
      let response;
      const answer = currentQuestion.answer;

      // Skip API calls if in test mode
      if (!inTestMode) {
        if (answer?.id) {
          response = await updateAnswer(inviteID, answer.id, answer.answer);
        } else if (answer) {
          response = await saveAnswer(
            inviteID,
            currentQuestion.id,
            answer.answer
          );
        } else {
          response = await saveAnswer(inviteID, currentQuestion.id, null);
        }

        // Update survey with server response
        setSurvey(prev => {
          const updated = { ...prev };
          const questions = [...prev.questions];
          questions[questionIndex] = {
            ...questions[questionIndex],
            answer: response.data,
          };
          updated.questions = questions;
          return updated;
        });
      }

      if (hasNext) {
        setQuestionIndex(prev => prev + 1);
      } else {
        setStatus('completed');
        if (!inTestMode) {
          handleRedirects(survey, response?.data || answer);
        }
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      setSaveError(messageFromSaveError(error));
    }
  }, [currentQuestion, isAnswered, inviteID, questionIndex, hasNext, survey, inTestMode]);

  const handleRedirects = (survey, latestAnswer) => {
    if (survey.redirect_conditions) {
      const redirect = survey.redirect_conditions.find((condition) => {
        const question = survey.questions.find(q => q.id === condition.question);
        const answerToCheck = question.id === currentQuestion.id ? latestAnswer : question.answer;
        return condition.options.includes(`${answerToCheck?.answer}`);
      });

      if (redirect) {
        window.location.href = redirect.url;
        return;
      }
    }

    if (survey.redirect_url) {
      window.location.href = survey.redirect_url;
    }
  };

  return {
    status,
    saveError,
    survey,
    currentQuestion,
    questionIndex,
    hasPrev,
    hasNext,
    isAnswered,
    setAnswer: updateLocalAnswer,
    goBack,
    goForward,
  };
};